import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

import { Trans } from '@/components'
import {
  BILL_SOURCE_TYPE_DISPLAY,
  BILL_SUBTYPE_DISPLAY,
  BILL_TYPE_DISPLAY,
  BILL_TYPE_SUBTYPE_DISPLAY,
  isAssetTransferHold,
} from '@/models/bill'
import { getBillDisplayTimeSeconds } from '@/utils/billingTime'
import { formatDatePreset, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { getTransferDisplay } from './transferDisplay'

interface BillData {
  resID?: string
  displayID?: string
  billTime?: { seconds: number }
  amount?: number
  type?: number
  subType?: number
  sourceType?: number
  Info?: any
  userID?: number
  timeInfo?: {
    endTime?: { seconds: number }
  }
  user?: {
    resID: string
    user: {
      name: string
      nickname?: string
      role?: number
    }
  }
  // Team billing structure
  bill?: {
    resID?: string
    displayID?: string
    billTime: { seconds: number }
    amount: number
    type: number
    subType: number
    sourceType?: number
    Info: any
    userID?: number
    timeInfo?: {
      endTime?: { seconds: number }
    }
  }
}

interface BillingTableProps {
  bills: BillData[]
  showMemberColumn?: boolean
}

export function BillingTable({ bills, showMemberColumn = false }: BillingTableProps) {
  const { t } = useTranslation('billing')

  const formatBillData = (data: BillData) => {
    // Handle team billing structure (nested bill object)
    const bill = data.bill || data
    const memberName = getDisplayName(data.user?.user.nickname, data.user?.user.name)

    const { pointsCent } = bill.Info?.RechargeInfo || {}
    const isUnsettled = bill.type === 1
    const isHold = isAssetTransferHold(bill)

    const rawTypeKey =
      BILL_TYPE_SUBTYPE_DISPLAY[`${bill.type}-${bill.subType}`] ||
      BILL_SUBTYPE_DISPLAY[bill.subType || 0] ||
      BILL_SOURCE_TYPE_DISPLAY[bill.sourceType ?? -1] ||
      BILL_TYPE_DISPLAY[bill.type || 0] ||
      '-'
    let typeStr = t(`typeStr.${rawTypeKey}`, { defaultValue: rawTypeKey })
    let displayMemberName = memberName

    const transferInfo = bill.Info?.TransferInfo
    if (transferInfo && bill.subType === 1) {
      const transferDisplay = getTransferDisplay({
        transferInfo,
        billUserId: bill.userID ?? data.userID,
        memberName,
        t,
      })

      if (transferDisplay) {
        typeStr = transferDisplay.typeStr
        displayMemberName = transferDisplay.memberName
      }
    }

    const paymentStr =
      bill.Info !== null && bill.Info?.RechargeInfo
        ? `${(pointsCent / 100).toFixed(3)} ${Math.abs(Number((pointsCent / 100).toFixed(3))) > 1 ? 'Credits' : 'Credit'}`
        : typeStr === 'Recharge' && bill.Info === null
          ? formatPrice(
              bill.amount || 0,
              false,
              Math.abs(bill.amount || 0) > 100 ? 'Credits' : 'Credit',
            )
          : formatPrice(
              bill.amount || 0,
              false,
              Math.abs(bill.amount || 0) > 100 ? 'Credits' : 'Credit',
            )

    return {
      bill,
      isUnsettled,
      isHold,
      typeStr,
      paymentStr,
      memberName: displayMemberName,
    }
  }

  return (
    <table className="table mt-4 w-full">
      <thead>
        <tr>
          <th className="text-left">
            <span className="t-sb text-secondary">
              <Trans i18nKey="team.billing.tableHeader.time">Time</Trans>
            </span>
          </th>
          <th className="text-left" style={{ minWidth: showMemberColumn ? '320px' : '360px' }}>
            <span className="t-sb text-secondary">
              <Trans i18nKey="team.billing.tableHeader.name">Name</Trans>
            </span>
          </th>
          {showMemberColumn && (
            <th className="text-left" style={{ minWidth: '220px' }}>
              <span className="t-sb text-secondary">
                <Trans i18nKey="team.billing.tableHeader.member">Member</Trans>
              </span>
            </th>
          )}
          <th className="text-left">
            <span className="t-sb text-secondary">
              <Trans i18nKey="team.billing.tableHeader.status">Status</Trans>
            </span>
          </th>
          <th className="!text-right">
            <span className="t-sb text-secondary">
              <Trans i18nKey="team.billing.tableHeader.payment">Payment</Trans>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        {bills.map((data, idx) => {
          const { bill, isUnsettled, isHold, typeStr, paymentStr, memberName } =
            formatBillData(data)

          return (
            <tr key={getBillRowKey(data, idx)}>
              <td>
                <div className="t-sb text-secondary">
                  {formatDatePreset((getBillDisplayTimeSeconds(bill) || 0) * 1000, 'second')}
                </div>
              </td>
              <td className="max-w-0">
                <Tooltip
                  title={<div className="t-sm">{typeStr}</div>}
                  placement="top"
                  mouseEnterDelay={0}
                >
                  <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-primary t-sb">
                    {typeStr}
                  </div>
                </Tooltip>
              </td>
              {showMemberColumn && (
                <td className="max-w-0">
                  <Tooltip
                    title={<div className="t-sm">{memberName}</div>}
                    placement="top"
                    mouseEnterDelay={0}
                  >
                    <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-primary t-sb">
                      {memberName}
                    </div>
                  </Tooltip>
                </td>
              )}
              <td
                className={cn('t-sb', {
                  'text-highlight': isUnsettled,
                  'text-secondary': isHold,
                  'text-success': !isUnsettled && !isHold,
                })}
              >
                {isHold ? (
                  <Tooltip title={t('status.holdHint')} placement="top" mouseEnterDelay={0}>
                    <span className="cursor-help underline decoration-dotted">
                      {t('status.hold')}
                    </span>
                  </Tooltip>
                ) : isUnsettled ? (
                  t('status.ongoing')
                ) : (
                  t('status.settled')
                )}
              </td>
              <td className={cn('text-right t-sb', { 'text-secondary': isHold })}>{paymentStr}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function getDisplayName(nickname?: string, name?: string) {
  const normalizedNickname = nickname?.trim()
  if (normalizedNickname) return normalizedNickname

  const normalizedName = name?.trim()
  if (normalizedName) {
    const at = normalizedName.indexOf('@')
    return at > 0 ? normalizedName.slice(0, at) : normalizedName
  }

  return '-'
}

function getBillRowKey(data: BillData, idx: number) {
  return (
    data.bill?.resID ??
    data.bill?.displayID ??
    data.resID ??
    data.displayID ??
    `${data.userID ?? data.bill?.userID ?? 'bill'}-${data.bill?.billTime?.seconds ?? data.billTime?.seconds ?? idx}-${idx}`
  )
}
